/**
 * Audit payloads decode from protobuf `Any` on the backend, so old/new field
 * shapes can vary across schema versions (see docs/backend-findings.md and
 * SPEC.md §5.9). This diff is intentionally defensive: it tolerates a nested
 * `{ data: {...} }` wrapper (how the real audit trigger stores rows) or a
 * flat object, and shows unknown fields raw rather than hiding them.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSONRecord = Record<string, any>;

export interface FieldDiff {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

// Already surfaced via the event's own timestamp/actor and version-transition
// label, so repeating them as diff rows would be redundant noise.
const OMITTED_FIELDS = new Set(['version', 'lastModified', 'lastModifiedBy']);

function unwrap(payload: unknown): JSONRecord {
  if (payload && typeof payload === 'object') {
    const record = payload as JSONRecord;
    if (record.data && typeof record.data === 'object') return record.data as JSONRecord;
    return record;
  }
  return {};
}

export function diffAuditPayloads(oldPayload: unknown, newPayload: unknown): FieldDiff[] {
  const oldData = unwrap(oldPayload);
  const newData = unwrap(newPayload);
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    if (OMITTED_FIELDS.has(key)) continue;
    const oldValue: unknown = oldData[key];
    const newValue: unknown = newData[key];
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      diffs.push({ field: key, oldValue, newValue });
    }
  }
  return diffs.sort((a, b) => a.field.localeCompare(b.field));
}

export function extractVersion(payload: unknown): number | undefined {
  const data = unwrap(payload);
  return typeof data.version === 'string' || typeof data.version === 'number' ? Number(data.version) : undefined;
}

export function formatDiffValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

const SITE_ID_FIELDS = new Set(['siteId', 'locationSiteId', 'site_id', 'location_site_id']);

export function formatItemAuditValue(
  field: string,
  value: unknown,
  siteCodeById: ReadonlyMap<string, string>,
): string {
  if (SITE_ID_FIELDS.has(field) && typeof value === 'string') {
    return siteCodeById.get(value) ?? formatDiffValue(value);
  }
  return formatDiffValue(value);
}
